import { UserIdentityProvider } from '@activepieces/shared';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { authenticationApi } from '@/lib/authentication-api';
import { authenticationSession } from '@/lib/authentication-session';
import { kizeoMessaging } from '@/app/routes/embed/kizeo/messaging';

export const useKizeoAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);

  const {
    mutate: signIn,
    isPending: isSigningIn,
    error: signInError,
  } = useMutation({
    mutationFn: authenticationApi.signInWithKuser,
    onSuccess: (response) => {
      authenticationSession.saveResponse(response);
      setIsRegistered(true);
      setIsAuthenticated(true);
    },
    onError: (error) => {
      console.error(`KUSER sign-in error: ${error}`);
    },
    onSettled: () => {
      setTimeout(() => {
        kizeoMessaging.sendKhubReadyMessage();
      }, 200);
    },
  });

  const {
    mutate: checkKuserInfo,
    isPending: isCheckingKuser,
    error: kuserInfoError,
  } = useMutation({
    mutationFn: authenticationApi.getCurrentKuserInfo,
    onSuccess: ({ userId, isRegistered }) => {
      setIsRegistered(isRegistered);

      if (!isRegistered) {
        return;
      }

      const currentUser = authenticationSession.getCurrentUser();

      if (
        currentUser?.provider === UserIdentityProvider.KIZEO &&
        userId === currentUser?.externalId
      ) {
        setIsAuthenticated(true);
        return;
      }
      authenticationSession.clearSession();
      signIn();
    },
    onError: (error) => {
      console.error(`KUSER info check error: ${error}`);
      setIsRegistered(false);
      authenticationSession.clearSession();
    },
    onSettled: () => {
      setTimeout(() => {
        kizeoMessaging.sendKhubReadyMessage();
      }, 200);
    },
  });

  return {
    authError: !!signInError || !!kuserInfoError,
    isPending: isSigningIn || isCheckingKuser,
    isAuthenticated,
    isRegistered,
    checkRegistration: checkKuserInfo,
    signIn,
  };
};
